/**
 * Network Detection Panel Component
 *
 * Displays carrier network analysis showing connections through
 * shared VINs, contacts, addresses, or officers.
 *
 * This is a UNIQUE feature - NO competitor has network detection!
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Network,
  AlertTriangle,
  CheckCircle,
  Users,
  Truck,
  Building,
  Phone,
  Mail,
  MapPin,
  User,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Shield,
  AlertCircle,
  Hash,
  ChevronRight,
} from 'lucide-react';
import { LoadingSpinner } from './shared/LoadingSpinner';
import { EmptyState } from './shared/EmptyState';
import { networkApi } from '../services/api';
import { logger } from '../utils/logger';
import '../styles/modern.css';

interface NetworkCarrier {
  dotNumber: number;
  carrierName: string;
  state: string;
  fleetSize: number;
  safetyRating: string | null;
  connectionType: string[];
  connectionCount: number;
}

interface NetworkConnection {
  fromDot: number;
  toDot: number;
  connectionTypes: string[];
  sharedVins?: string[];
  sharedContacts?: string[];
  strength?: number;
}

interface NetworkData {
  networkDetected: boolean;
  network?: {
    networkId: string;
    carriers: NetworkCarrier[];
    connections: NetworkConnection[];
    summary?: {
      totalCarriers: number;
      totalConnections: number;
      sharedVinsCount: number;
      riskLevel: string;
    };
  };
  message?: string;
}

interface NetworkPanelProps {
  dotNumber: number;
}

export const NetworkPanel: React.FC<NetworkPanelProps> = ({ dotNumber }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [networkData, setNetworkData] = useState<NetworkData | null>(null);
  const [expandedCarriers, setExpandedCarriers] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (dotNumber) {
      fetchNetworkData();
    }
  }, [dotNumber]);

  const fetchNetworkData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await networkApi.detectCarrierNetwork(dotNumber);
      setNetworkData(response as unknown as NetworkData);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to detect network';
      logger.error('Network detection error:', { error: err, dotNumber });
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const toggleCarrierExpansion = (dot: number) => {
    const newExpanded = new Set(expandedCarriers);
    if (newExpanded.has(dot)) {
      newExpanded.delete(dot);
    } else {
      newExpanded.add(dot);
    }
    setExpandedCarriers(newExpanded);
  };

  interface ConnectionTypeInfo {
    label: string;
    icon: React.ComponentType<{ className?: string; size?: number }>;
    color: string;
  }

  const getConnectionTypeLabel = (type: string): ConnectionTypeInfo => {
    switch (type) {
      case 'shared_vins':
        return { label: 'Shared Vehicles', icon: Truck, color: 'var(--color-danger)' };
      case 'shared_phone':
        return { label: 'Same Phone', icon: Phone, color: 'var(--color-warning)' };
      case 'shared_email':
        return { label: 'Same Email', icon: Mail, color: 'var(--color-warning)' };
      case 'shared_address':
        return { label: 'Same Address', icon: MapPin, color: 'var(--color-danger)' };
      case 'shared_officer':
        return { label: 'Same Officer', icon: User, color: 'var(--color-danger)' };
      case 'primary':
        return { label: 'Primary Carrier', icon: Building, color: 'var(--color-primary)' };
      default:
        return { label: type, icon: Network, color: 'var(--color-info)' };
    }
  };

  const getRiskLevelColor = (level: string): string => {
    switch (level?.toUpperCase()) {
      case 'CRITICAL':
        return 'var(--color-danger)';
      case 'HIGH':
        return '#7d561c';
      case 'MEDIUM':
        return 'var(--color-warning)';
      case 'LOW':
        return 'var(--color-success)';
      default:
        return 'var(--color-info)';
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <LoadingSpinner message="Analyzing fraud networks..." />
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState
        title="Error Loading Network Data"
        description={error}
        icon={AlertCircle}
        action={{
          label: 'Retry',
          onClick: fetchNetworkData
        }}
      />
    );
  }

  if (!networkData?.networkDetected || !networkData?.network) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          backgroundColor: 'rgba(93, 113, 72, 0.1)',
          marginBottom: '1.5rem',
        }}>
          <CheckCircle size={40} style={{ color: 'var(--color-success)' }} />
        </div>
        <h3 className="heading-3" style={{ marginBottom: '0.5rem' }}>No Network Connections Detected</h3>
        <p style={{ color: 'var(--color-text-muted)', marginBottom: '1rem' }}>
          This carrier does not appear to be part of a shell company network.
        </p>
        <div style={{
          display: 'inline-block',
          padding: '0.75rem 1.5rem',
          backgroundColor: 'rgba(93, 113, 72, 0.1)',
          borderRadius: '8px',
          marginTop: '1rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Shield size={20} style={{ color: 'var(--color-success)' }} />
            <span style={{ fontWeight: 600, color: 'var(--color-success)' }}>
              ✓ No fraud network indicators
            </span>
          </div>
        </div>
      </div>
    );
  }

  const { network } = networkData;
  const primaryCarrier = network.carriers.find(c => c.connectionType.includes('primary'));
  const relatedCarriers = network.carriers.filter(c => !c.connectionType.includes('primary'));

  return (
    <div style={{ padding: '0.75rem' }}>
      {/* Header */}
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            backgroundColor: 'rgba(179, 65, 44, 0.1)',
          }}>
            <Network size={16} style={{ color: 'var(--color-danger)' }} />
          </div>
          <div>
            <h3 className="heading-3" style={{ marginBottom: '0.125rem', fontSize: '1rem' }}>
              Network Connections Detected
            </h3>
            <p style={{ color: 'var(--color-text-muted)', fontSize: '0.75rem' }}>
              ID: {network.networkId}
            </p>
          </div>
        </div>

        {/* Summary Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: '0.5rem',
          marginTop: '0.75rem',
        }}>
          <div className="stat-card" style={{ padding: '0.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', marginBottom: '0.25rem' }}>
              <Building size={12} style={{ color: 'var(--color-primary)' }} />
              <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)' }}>
                Related Carriers
              </span>
            </div>
            <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>
              {network.carriers.length}
            </div>
          </div>

          <div className="stat-card" style={{ padding: '0.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', marginBottom: '0.25rem' }}>
              <Network size={12} style={{ color: 'var(--color-warning)' }} />
              <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)' }}>
                Connections
              </span>
            </div>
            <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>
              {network.connections.length}
            </div>
          </div>

          <div className="stat-card" style={{ padding: '0.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', marginBottom: '0.25rem' }}>
              <Truck size={12} style={{ color: 'var(--color-danger)' }} />
              <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)' }}>
                Shared VINs
              </span>
            </div>
            <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>
              {network.connections.reduce((sum, conn) => sum + (conn.sharedVins?.length || 0), 0)}
            </div>
          </div>

          {network.summary?.riskLevel && (
            <div className="stat-card" style={{
              borderLeft: `3px solid ${getRiskLevelColor(network.summary.riskLevel)}`,
              padding: '0.5rem',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', marginBottom: '0.25rem' }}>
                <AlertTriangle size={12} style={{ color: getRiskLevelColor(network.summary.riskLevel) }} />
                <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)' }}>
                  Risk Level
                </span>
              </div>
              <div style={{
                fontSize: '1rem',
                fontWeight: 700,
                color: getRiskLevelColor(network.summary.riskLevel),
              }}>
                {network.summary.riskLevel}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Network Alert */}
      <div style={{
        padding: '0.625rem',
        backgroundColor: 'rgba(179, 65, 44, 0.08)',
        borderLeft: '3px solid var(--color-danger)',
        borderRadius: '6px',
        marginBottom: '1rem',
      }}>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <AlertTriangle size={16} style={{ color: 'var(--color-danger)', flexShrink: 0, marginTop: '1px' }} />
          <div>
            <div style={{ fontWeight: 600, marginBottom: '0.25rem', color: 'var(--color-danger)', fontSize: '0.8125rem' }}>
              Network Connections Identified
            </div>
            <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginBottom: '0.25rem', lineHeight: 1.4 }}>
              This carrier shares {network.connections.reduce((sum, conn) => sum + (conn.sharedVins?.length || 0), 0)} vehicles with {relatedCarriers.length} other carrier{relatedCarriers.length !== 1 ? 's' : ''}. Review each connection before proceeding.
            </p>
            <div style={{ fontSize: '0.75rem', fontWeight: 600 }}>
              ⚠️ Recommended: Verify each connected carrier independently
            </div>
          </div>
        </div>
      </div>

      {/* Primary Carrier */}
      {primaryCarrier && (
        <div style={{ marginBottom: '1rem' }}>
          <h4 style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.875rem', fontWeight: 600 }}>
            <Building size={14} />
            Primary Carrier
          </h4>
          <div className="card" style={{
            padding: '0.75rem',
            border: '2px solid var(--color-primary)',
            backgroundColor: 'rgba(35, 33, 29, 0.04)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
              <div>
                <div style={{ fontSize: '0.9375rem', fontWeight: 700, marginBottom: '0.25rem' }}>
                  {primaryCarrier.carrierName}
                </div>
                <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                  <span><Hash size={12} style={{ display: 'inline', verticalAlign: 'middle' }} /> DOT {primaryCarrier.dotNumber}</span>
                  <span><MapPin size={12} style={{ display: 'inline', verticalAlign: 'middle' }} /> {primaryCarrier.state}</span>
                  <span><Truck size={12} style={{ display: 'inline', verticalAlign: 'middle' }} /> {primaryCarrier.fleetSize} units</span>
                </div>
              </div>
              <div className="badge badge-primary" style={{ fontSize: '0.7rem', padding: '0.25rem 0.5rem' }}>Current</div>
            </div>
          </div>
        </div>
      )}

      {/* Related Carriers */}
      <div>
        <h4 style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.875rem', fontWeight: 600 }}>
          <Users size={14} />
          Connected Carriers ({relatedCarriers.length})
        </h4>

        {relatedCarriers.length === 0 ? (
          <EmptyState
            title="No Connections Found"
            description="This carrier has no network connections"
            icon={Network}
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {relatedCarriers.map((carrier) => {
              const connection = network.connections.find(
                c => c.toDot === carrier.dotNumber || c.fromDot === carrier.dotNumber
              );
              const isExpanded = expandedCarriers.has(carrier.dotNumber);
              const sharedVinsCount = connection?.sharedVins?.length || 0;

              return (
                <div
                  key={carrier.dotNumber}
                  className="card"
                  style={{
                    padding: '0.75rem',
                    borderLeft: `3px solid ${sharedVinsCount > 10 ? 'var(--color-danger)' : sharedVinsCount > 5 ? 'var(--color-warning)' : 'var(--color-info)'}`,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onClick={() => navigate(`/dashboard?dot=${carrier.dotNumber}`)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {/* Carrier Header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                        <div style={{ fontSize: '0.9375rem', fontWeight: 700 }}>
                          {carrier.carrierName}
                        </div>
                        <ExternalLink size={12} style={{ color: 'var(--color-primary)' }} />
                      </div>
                      <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                        <span><Hash size={12} style={{ display: 'inline', verticalAlign: 'middle' }} /> DOT {carrier.dotNumber}</span>
                        <span><MapPin size={12} style={{ display: 'inline', verticalAlign: 'middle' }} /> {carrier.state}</span>
                        <span><Truck size={12} style={{ display: 'inline', verticalAlign: 'middle' }} /> {carrier.fleetSize} units</span>
                      </div>
                    </div>

                    {carrier.safetyRating && (
                      <div className={`badge badge-${
                        carrier.safetyRating === 'S' ? 'success' :
                        carrier.safetyRating === 'C' ? 'warning' :
                        carrier.safetyRating === 'U' ? 'danger' : 'secondary'
                      }`} style={{ fontSize: '0.7rem', padding: '0.25rem 0.5rem' }}>
                        Safety: {carrier.safetyRating}
                      </div>
                    )}
                  </div>

                  {/* Connection Types */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', marginBottom: '0.5rem' }}>
                    {carrier.connectionType.map((type) => {
                      const typeInfo = getConnectionTypeLabel(type);
                      const Icon = typeInfo.icon;
                      return (
                        <div
                          key={type}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            padding: '0.25rem 0.5rem',
                            backgroundColor: `${typeInfo.color}12`,
                            borderRadius: '4px',
                            fontSize: '0.7rem',
                            fontWeight: 500,
                            color: typeInfo.color,
                          }}
                        >
                          <Icon size={11} />
                          {typeInfo.label}
                        </div>
                      );
                    })}
                  </div>

                  {/* Shared VINs Preview */}
                  {connection?.sharedVins && connection.sharedVins.length > 0 && (
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleCarrierExpansion(carrier.dotNumber);
                      }}
                      style={{
                        padding: '0.5rem',
                        backgroundColor: 'rgba(179, 65, 44, 0.04)',
                        borderRadius: '4px',
                        border: '1px solid rgba(179, 65, 44, 0.15)',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: isExpanded ? '0.5rem' : '0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontWeight: 600, fontSize: '0.75rem' }}>
                          <Truck size={12} style={{ color: 'var(--color-danger)' }} />
                          {sharedVinsCount} Shared Vehicle{sharedVinsCount !== 1 ? 's' : ''}
                        </div>
                        {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </div>

                      {/* Expanded VIN List */}
                      {isExpanded && (
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                          gap: '0.375rem',
                          marginTop: '0.5rem',
                        }}>
                          {connection.sharedVins.slice(0, 20).map((vin) => (
                            <div
                              key={vin}
                              style={{
                                padding: '0.375rem',
                                backgroundColor: '#fbf9f4',
                                border: '1px solid #e2ddd1',
                                borderRadius: '3px',
                                fontSize: '0.6875rem',
                                fontFamily: 'monospace',
                                textAlign: 'center',
                              }}
                            >
                              {vin}
                            </div>
                          ))}
                          {connection.sharedVins.length > 20 && (
                            <div style={{
                              padding: '0.375rem',
                              fontSize: '0.6875rem',
                              color: 'var(--color-text-muted)',
                              textAlign: 'center',
                            }}>
                              +{connection.sharedVins.length - 20} more
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Click hint */}
                  <div style={{
                    marginTop: '0.5rem',
                    paddingTop: '0.5rem',
                    borderTop: '1px solid #e2ddd1',
                    fontSize: '0.7rem',
                    color: 'var(--color-primary)',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                  }}>
                    Click to view carrier profile
                    <ChevronRight size={11} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Network Explanation */}
      <div style={{
        marginTop: '1rem',
        padding: '0.75rem',
        backgroundColor: 'rgba(35, 33, 29, 0.04)',
        border: '1px solid rgba(35, 33, 29, 0.15)',
        borderRadius: '6px',
      }}>
        <h5 style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.8125rem' }}>
          <Shield size={14} style={{ color: 'var(--color-primary)' }} />
          Understanding Network Connections
        </h5>
        <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', lineHeight: 1.5, marginBottom: '0.5rem' }}>
          Network analysis identifies carriers with operational relationships. Shared vehicles between carriers may indicate legitimate fleet management or require additional verification for compliance purposes.
        </p>
        <div style={{ marginTop: '0.5rem', padding: '0.5rem', backgroundColor: '#fbf9f4', borderRadius: '4px' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.25rem' }}>
            Connection Types:
          </div>
          <ul style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', margin: 0, paddingLeft: '1.25rem', lineHeight: 1.6 }}>
            <li>Shared vehicles across multiple carriers</li>
            <li>Common contact information</li>
            <li>Shared management or officers</li>
            <li>Coordinated operational patterns</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default NetworkPanel;
